import { action } from 'mobx';
import * as Prismic from '@prismicio/client';
import { Logger } from 'winston';
import { AnyObject, crawlData, DataHandler } from '../../components/project-happy/utilities/objects';
import { resolveImageUrl } from '../../components/project-happy/utilities/prismic';
import { getUid } from '../../components/project-happy/utilities/string';
import { EnvModel } from '../env/model';
import { ImageModel } from '../image/model';
import { RouterModel } from '../router/model';

type LickdPrismicOptions = {
  resolveImages?: boolean;
};

export type PrismicPayload = { id: string; uid: string; type: string; data: AnyObject };

type PrismicResults = { results: PrismicPayload[] };

const splitOptions = ({
  resolveImages,
  ...apiOptions
}: AnyObject): { responseOptions: LickdPrismicOptions; apiOptions: AnyObject } => ({
  responseOptions: { resolveImages },
  apiOptions,
});

export class PrismicController {
  private defaultQueryOptions: { ref?: string } = {};
  private imageResolver: DataHandler;
  private prismicClient: Prismic.Client;

  constructor(private env: EnvModel, private router: RouterModel, private image: ImageModel, private logger: Logger) {
    this.imageResolver = resolveImageUrl(image);
    if (env.prismicRefOverride) {
      this.defaultQueryOptions.ref = env.prismicRefOverride;
    }
    this.prismicClient = Prismic.createClient(this.env.prismicEndpoint, {
      fetchOptions: {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'User-Agent': this.env.userAgent,
        },
      },
      fetch: async (url) => {
        // For some reason, Prismic Client adds an extra `/documents` into the url for single document requests, which causes a fetch error
        // This is likely something to do with the fact we proxy all Prismic requests via API backend, and the endpoint already has a `/documents` as part of the URL
        // Temporary (gross) solution is to strip duplicate documents segments
        const strippedUrl = url.includes('/documents/documents')
          ? url.replace('/documents/documents', '/documents')
          : url;
        const result = await fetch(strippedUrl);

        return result;
      },
    });
  }

  private resolveOptions = (options?: any) => Object.assign({}, this.defaultQueryOptions, options);

  private resolveResponse = (data: any, { resolveImages }: LickdPrismicOptions) => {
    if (resolveImages === true) {
      crawlData(data, [this.imageResolver]);
    }

    return data;
  };

  @action
  getSingle = async (key: string, options: AnyObject = {}): Promise<PrismicPayload> => {
    const prismicRequest = { id: getUid(), key, options: splitOptions(options) };

    this.logger.info('Prismic request - start', { prismicRequest });

    try {
      const response = await this.prismicClient.getSingle(
        prismicRequest.key,
        this.resolveOptions(prismicRequest.options.apiOptions)
      );

      return this.resolveResponse(response, prismicRequest.options.responseOptions);
    } finally {
      this.logger.info('Prismic request - finished', { prismicRequest });
    }
  };

  @action
  getByTag = async (tag: string, options: AnyObject = {}): Promise<PrismicResults> => {
    const prismicRequest = {
      id: getUid(),
      predicate: [Prismic.predicate.at('document.type', tag)],
      options: splitOptions(options),
    };

    this.logger.info('Prismic request - start', { prismicRequest });

    try {
      const response = await this.prismicClient.get({
        predicates: prismicRequest.predicate,
        ...this.resolveOptions(prismicRequest.options.apiOptions),
      });

      return this.resolveResponse(response, prismicRequest.options.responseOptions);
    } finally {
      this.logger.info('Prismic request - finished', { prismicRequest });
    }
  };

  @action
  query = async (predicate: any, options: AnyObject = {}): Promise<PrismicResults> => {
    const prismicRequest = { id: getUid(), predicate, options: splitOptions(options) };

    this.logger.info('Prismic request - start', { prismicRequest });

    try {
      const response = await this.prismicClient.get({
        predicates: prismicRequest.predicate,
        ...this.resolveOptions(prismicRequest.options.apiOptions),
      });

      return this.resolveResponse(response, prismicRequest.options.responseOptions);
    } finally {
      this.logger.info('Prismic request - finished', { prismicRequest });
    }
  };
}
